// toast/ToastContext.tsx
import React, { createContext, useCallback, useContext, useState } from 'react';
import CustomToast from '../VehicleEditor/CustomToast';

type Severity = 'success' | 'error' | 'info' | 'warning';

type ShowToastArgs = {
  message: React.ReactNode;
  severity: Severity;
  autoHideMs?: number | false;
  onSave?: () => void;
  saveLabel?: string;
  saveDisabled?: boolean;
  anchor?: { vertical: 'top' | 'bottom'; horizontal: 'left' | 'center' | 'right' };
};

type ToastContextType = {
  showToast: (args: ShowToastArgs) => void;
  hideToast: () => void;
  // convenience helpers
  success: (msg: React.ReactNode, opts?: Omit<ShowToastArgs, 'message'|'severity'>) => void;
  info:    (msg: React.ReactNode, opts?: Omit<ShowToastArgs, 'message'|'severity'>) => void;
  error:   (msg: React.ReactNode, opts?: Omit<ShowToastArgs, 'message'|'severity'>) => void;
  warning: (msg: React.ReactNode, opts?: Omit<ShowToastArgs, 'message'|'severity'>) => void;
};

const ToastContext = createContext<ToastContextType | null>(null);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState<ShowToastArgs | null>(null);

  const hideToast = useCallback(() => setOpen(false), []);
  const showToast = useCallback((args: ShowToastArgs) => {
    setCfg(args);
    setOpen(true);
  }, []);

  const helpers = {
    success: (message: React.ReactNode, opts?: Omit<ShowToastArgs, 'message'|'severity'>) =>
      showToast({ message, severity: 'success', autoHideMs: 2500, ...opts }),
    info: (message: React.ReactNode, opts?: Omit<ShowToastArgs, 'message'|'severity'>) =>
      showToast({ message, severity: 'info', autoHideMs: 2500, ...opts }),
    error: (message: React.ReactNode, opts?: Omit<ShowToastArgs, 'message'|'severity'>) =>
      showToast({ message, severity: 'error', autoHideMs: 4000, ...opts }),
    warning: (message: React.ReactNode, opts?: Omit<ShowToastArgs, 'message'|'severity'>) =>
      showToast({ message, severity: 'warning', autoHideMs: 4000, ...opts }),
  };

  return (
    <ToastContext.Provider value={{ showToast, hideToast, ...helpers }}>
      {children}
      {cfg && (
        <CustomToast
          open={open}
          onClose={hideToast}
          message={cfg.message}
          severity={cfg.severity}
          autoHideMs={cfg.autoHideMs}
          onSave={cfg.onSave}
          saveLabel={cfg.saveLabel}
          saveDisabled={cfg.saveDisabled ?? false}
          anchor={cfg.anchor}
        />
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within <ToastProvider>');
  return ctx;
};
